import { Map, Pencil, PlayCircle, type LucideIcon } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import type {
  PlanAction,
  PlanStep,
  StartTreatmentPresetCycleType,
  StepStatus,
} from "../plan.types";
import { Hero, type HeroMenuAction, type HeroMetric } from "./hero/Hero";

const preCycleHeroBackground =
  require("@src/apps/moon/assets/images/precycle-hero-bronze-gradient.webp") as number;

export type PreCyclePrepStep = {
  id: string;
  title: string;
  icon?: string;
  status: StepStatus;
};

export type PreCycleHeroSnapshotItem = {
  label: string;
  value: string;
  note?: string;
  action?: PlanAction;
};

type Props = {
  activeStep: PlanStep | null;
  /** The first action for a guided pre-cycle surface. */
  nextAction?: PlanAction;
  completedSteps: number;
  totalSteps: number;
  eyebrow: string;
  title: string;
  snapshotItems: PreCycleHeroSnapshotItem[];
  phaseDescription: string;
  onAction: (action: PlanAction) => void;
  onOpenPlan: () => void;
  onEditPlan: () => void;
  eyebrowIcon?: LucideIcon;
  phaseLabel?: string;
  phaseIcon?: LucideIcon;
  planLabel?: string;
  progressCaption?: string;
  editPlanLabel?: string;
  showStartCycleAction?: boolean;
  startCyclePreset?: StartTreatmentPresetCycleType;
  openPlanAccessibilityLabel?: string;
  planAccessibilityLabel?: string;
};

export function PreCycleHeroCard({
  activeStep,
  nextAction,
  completedSteps,
  totalSteps,
  title,
  snapshotItems,
  phaseDescription,
  onAction,
  onOpenPlan,
  onEditPlan,
  planLabel,
  editPlanLabel,
  showStartCycleAction = false,
  startCyclePreset,
}: Props) {
  const t = useTranslations();
  const resolvedPlanLabel =
    planLabel ?? t("homepage.pre_cycle_hero_card.your_plan");
  const resolvedEditPlanLabel =
    editPlanLabel ?? t("homepage.pre_cycle_hero_card.edit_plan");
  const primaryAction = nextAction ?? activeStep?.actions[0] ?? null;
  const activeStepTitle = activeStep?.title.trim() ?? "";
  const isMedicalHistoryStep =
    activeStep?.id === "medical-history" ||
    activeStepTitle.toLowerCase().includes("medical history");
  const heroTitle = activeStep
    ? isMedicalHistoryStep
      ? t("homepage.pre_cycle_hero_card.complete_setup")
      : t("homepage.pre_cycle_hero_card.keep_shaping_plan")
    : title;
  const heroSummary = activeStep
    ? isMedicalHistoryStep
      ? t("homepage.pre_cycle_hero_card.start_with_history")
      : t("homepage.pre_cycle_hero_card.active_step_summary", {
          params: {
            activeStepTitle,
            subtitle:
              activeStep.subtitle ??
              t("homepage.pre_cycle_hero_card.fallback_subtitle"),
          },
        })
    : phaseDescription;
  const metrics: HeroMetric[] = [
    {
      label: t("homepage.pre_cycle_hero_card.your_path"),
      value:
        totalSteps > 0
          ? t(
              // FR (and other count-inflected locales) need the singular form
              // for 0/1 completed: "0 étape … terminée", not "0 étapes …
              // terminées". t() has no plural support, so pick the key by count.
              completedSteps <= 1
                ? "homepage.pre_cycle_hero_card.steps_complete_one"
                : "homepage.pre_cycle_hero_card.steps_complete",
              {
                params: {
                  completedSteps: String(completedSteps),
                  totalSteps: String(totalSteps),
                },
              },
            )
          : t("homepage.pre_cycle_hero_card.plan_not_set"),
    },
    ...snapshotItems
      .filter((item) => item.label.trim().toLowerCase() !== "next")
      .map((item): HeroMetric => {
        const action = item.action;

        return {
          label: item.label,
          value: item.value,
          note: item.note,
          onPress: action ? () => onAction(action) : undefined,
          accessibilityLabel: action?.label,
        };
      }),
  ];
  const menuActions: HeroMenuAction[] = [
    ...(showStartCycleAction
      ? [
          {
            icon: PlayCircle,
            label: t("you.my_journey.menu_start_cycle"),
            onPress: () =>
              onAction({
                label: t("you.my_journey.menu_start_cycle"),
                signal: "open-start-treatment-modal",
                ...(startCyclePreset
                  ? { params: { presetCycleType: startCyclePreset } }
                  : {}),
              }),
          },
        ]
      : []),
    {
      icon: Pencil,
      label: resolvedEditPlanLabel,
      onPress: onEditPlan,
    },
    {
      icon: Map,
      label: resolvedPlanLabel,
      onPress: onOpenPlan,
    },
  ];

  return (
    <Hero
      title={heroTitle}
      summary={heroSummary}
      metrics={metrics}
      metricVariant="tags"
      backgroundSource={preCycleHeroBackground}
      primaryAction={
        primaryAction
          ? {
              label: primaryAction.label,
              onPress: () => onAction(primaryAction),
            }
          : undefined
      }
      menuActions={menuActions}
      style={{ marginTop: 16 }}
    />
  );
}
